import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { fmt$, inputCls } from "./shared";

const REASONS = [
  ["consumption", "Consumption (internal use)"],
  ["destructive_testing", "Destructive testing"],
  ["damage", "Damage / write-off"],
  ["sample", "Sample issued"],
  ["stock_count", "Stock count (set counted qty)"],
  ["found", "Found / adjustment in"],
  ["other", "Other"],
];
const LABEL = Object.fromEntries(REASONS);
const blank = () => ({ sku: "", quantity: "", reason: "consumption", note: "", location_id: "" });

export function StockAdjustments({ products, locations = [], refreshKey, onChange }) {
  const [form, setForm] = useState(blank());
  const [rows, setRows] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const prod = products.find((p) => p.sku === form.sku);
  const [levels, setLevels] = useState([]);
  const activeLocs = locations.filter((l) => l.active !== false);
  const locId = form.location_id || activeLocs.find((l) => l.is_default)?.id || activeLocs[0]?.id || "";
  const atLoc = prod ? (levels.find((r) => r.sku === prod.sku)?.by_location?.[locId] ?? 0) : 0;

  useEffect(() => {
    api.get("/scm/stock-adjustments").then((r) => setRows(r.data)).catch(() => {});
    api.get("/scm/stock-levels").then((r) => setLevels(r.data.levels)).catch(() => {});
  }, [refreshKey]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/scm/stock-adjustments", { ...form, quantity: Number(form.quantity), location_id: locId || null });
      toast.success("Stock adjusted");
      setForm(blank());
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const preview = prod && form.quantity !== "" ? (form.reason === "stock_count" ? Number(form.quantity) : form.reason === "found" ? atLoc + Number(form.quantity) : atLoc - Number(form.quantity)) : null;

  return (
    <div data-testid="adjustments-panel">
      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-[1fr_160px_120px_220px_1fr_auto] gap-px bg-zinc-200 border-b border-zinc-950" data-testid="adjustment-form">
        <select value={form.sku} onChange={(e) => set("sku", e.target.value)} required data-testid="adj-sku-select" className={inputCls}>
          <option value="">— product —</option>
          {products.map((p) => <option key={p.id} value={p.sku}>{`${p.sku} · ${p.name} (stock ${p.stock})`}</option>)}
        </select>
        <select value={locId} onChange={(e) => set("location_id", e.target.value)} data-testid="adj-location-select" className={inputCls}>
          {activeLocs.map((l) => <option key={l.id} value={l.id}>{`${l.code} · ${l.name}`}</option>)}
        </select>
        <input type="number" min="1" step="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} required placeholder={form.reason === "stock_count" ? "Counted qty" : "Qty"} data-testid="adj-qty-input" className={`${inputCls} tnum`} />
        <select value={form.reason} onChange={(e) => set("reason", e.target.value)} data-testid="adj-reason-select" className={inputCls}>
          {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Note / reference" data-testid="adj-note-input" className={inputCls} />
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] flex items-center justify-center gap-1" data-testid="adj-submit-btn">
          <Plus size={14} /> POST
        </button>
      </form>
      {prod && (
        <div className="px-5 py-2 text-[10px] font-mono text-zinc-500 border-b border-zinc-200" data-testid="adj-preview">
          {prod.sku}: {atLoc} at this location ({prod.stock} total){preview !== null && <> → location after posting <span className={preview < 0 ? "text-[#D0021B]" : "text-zinc-950 font-semibold"}>{preview}</span></>}
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">Adj #</th>
            <th className="text-left px-4 py-2.5">Date</th>
            <th className="text-left px-4 py-2.5">SKU</th>
            <th className="text-left px-4 py-2.5">Location</th>
            <th className="text-left px-4 py-2.5">Reason</th>
            <th className="text-right px-4 py-2.5">Before</th>
            <th className="text-right px-4 py-2.5">Delta</th>
            <th className="text-right px-4 py-2.5">After</th>
            <th className="text-right px-4 py-2.5">Value</th>
            <th className="text-left px-4 py-2.5">Note</th>
          </tr>
        </thead>
        <tbody data-testid="adjustments-tbody">
          {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-4 text-xs text-zinc-400 font-mono">No internal stock transactions yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-zinc-100" data-testid={`adj-row-${r.adj_no}`}>
              <td className="px-4 py-2.5 tnum text-xs">{r.adj_no}</td>
              <td className="px-4 py-2.5 tnum text-xs text-zinc-500">{(r.created_at || "").slice(0, 10)}</td>
              <td className="px-4 py-2.5 tnum text-xs">{r.sku} <span className="text-zinc-500">· {r.product_name}</span></td>
              <td className="px-4 py-2.5 tnum text-xs text-zinc-500">{r.location_code || "—"}</td>
              <td className="px-4 py-2.5 text-zinc-600">{LABEL[r.reason] || r.reason}</td>
              <td className="px-4 py-2.5 text-right tnum">{r.stock_before}</td>
              <td className={`px-4 py-2.5 text-right tnum font-semibold ${r.delta < 0 ? "text-[#D0021B]" : "text-emerald-700"}`}>{r.delta > 0 ? "+" : ""}{r.delta}</td>
              <td className="px-4 py-2.5 text-right tnum">{r.stock_after}</td>
              <td className="px-4 py-2.5 text-right tnum text-zinc-600">{fmt$(r.value_impact)}</td>
              <td className="px-4 py-2.5 text-zinc-500 text-xs">{r.note}<div className="text-zinc-400">{r.created_by}</div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
